/*
Purpose: Right-hand panel: tutor filter, status chips, notes, and
count-only request summary (non-functional — open Requests for actions).
*/

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import SearchIcon from '@mui/icons-material/Search';

import {
  DELIVERY_MODE,
  DISPLAY_STATUS,
  DISPLAY_STATUS_META,
  FILTER_CHIP,
  FILTER_CHIP_META,
  FILTERABLE_STATUSES,
  TUTOR_LANE_COLORS,
} from '../constants';
import { modeIconFor, statusIconFor } from './badges';
import { INBOX_TABS } from '../request-tabs';

export default function FilterPanel({
  lanes,
  selectedTutorKeys,
  onToggleTutor,
  onSelectAllTutors,
  onTutorClick,
  statusFilter,
  onToggleStatus,
  notes,
  onAddNote,
  onDeleteNote,
  isNoteSubmitting,
  requestCounts,
  onOpenRequest,
}) {
  const [tutorQuery, setTutorQuery] = useState('');
  const [noteDraft, setNoteDraft] = useState('');

  // Diagnostics is a Status chip only — keep it out of the tutor list/search.
  const filteredLanes = lanes.filter(
    (lane) =>
      !lane.isDiagnostics &&
      lane.tutorName.toLowerCase().includes(tutorQuery.trim().toLowerCase())
  );

  const allTutorsSelected = selectedTutorKeys.length === 0;

  function handleNoteSubmit(event) {
    event.preventDefault();
    const text = noteDraft.trim();
    if (!text) {
      return;
    }
    onAddNote(text);
    setNoteDraft('');
  }

  return (
    <aside className="cv2-panel cv2-panel--filters">
      <div className="cv2-panel__head">
        <h2 className="cv2-panel__title">
          <FilterAltOutlinedIcon className="cv2-icon" />
          Filters
        </h2>
        <span className="cv2-panel__count">
          {allTutorsSelected ? 'All tutors' : `${selectedTutorKeys.length} selected`}
        </span>
      </div>

      <div className="cv2-panel__body">
        <h3 className="cv2-panel__section-title">
          <InboxOutlinedIcon className="cv2-icon" style={{ fontSize: 14 }} />
          Requests
        </h3>
        <div className="cv2-filter-req" role="list" aria-label="Request counts">
          {INBOX_TABS.map((tab) => {
            const count = (requestCounts && requestCounts[tab.countKey]) || 0;
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                className={`cv2-filter-req__item${count > 0 ? ' has-items' : ''}`}
                onClick={() => onOpenRequest(tab.id)}
              >
                <TabIcon className="cv2-icon cv2-filter-req__icon" />
                <span className="cv2-filter-req__label">{tab.label}</span>
                <span
                  className={`cv2-filter-req__count${
                    count > 0 ? ' cv2-filter-req__count--alert' : ''
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <h3 className="cv2-panel__section-title">Tutors</h3>
        <div className="cv2-search">
          <SearchIcon className="cv2-icon" />
          <input
            type="search"
            value={tutorQuery}
            onChange={(event) => setTutorQuery(event.target.value)}
            placeholder="Search tutors"
            aria-label="Search tutors"
          />
        </div>
        <div className="cv2-tutorlist">
          <label className="cv2-tutorlist__row">
            <input type="checkbox" checked={allTutorsSelected} onChange={onSelectAllTutors} />
            <span className="cv2-tutorlist__name">All tutors</span>
          </label>

          {filteredLanes.map((lane) => {
            const laneColor = TUTOR_LANE_COLORS[lane.colorIndex % TUTOR_LANE_COLORS.length];
            return (
              <div className="cv2-tutorlist__row" key={lane.tutorKey}>
                <input
                  type="checkbox"
                  checked={allTutorsSelected || selectedTutorKeys.includes(lane.tutorKey)}
                  onChange={() => onToggleTutor(lane.tutorKey)}
                  aria-label={`Show ${lane.tutorName}`}
                />
                <span className="cv2-tutorlist__swatch" style={{ background: laneColor.edge }} />
                <button
                  type="button"
                  className="cv2-tutorlist__name cv2-tutorlist__name--link"
                  title={`Open ${lane.tutorName}`}
                  onClick={() =>
                    onTutorClick({ tutorKey: lane.tutorKey, tutorName: lane.tutorName })
                  }
                >
                  {lane.tutorName}
                  <small>
                    {lane.lessonCount} student{lane.lessonCount === 1 ? '' : 's'}
                  </small>
                </button>
              </div>
            );
          })}

          {filteredLanes.length === 0 ? (
            <span className="cv2-legend__hint">No tutors match &ldquo;{tutorQuery}&rdquo;.</span>
          ) : null}
        </div>

        <h3 className="cv2-panel__section-title">Status</h3>
        <div className="cv2-chips">
          {FILTERABLE_STATUSES.map((status) => {
            const isFlexiChip = status === FILTER_CHIP.FLEXI;
            const isInCentreChip = status === FILTER_CHIP.IN_CENTRE;
            const isDiagnosticsChip = status === FILTER_CHIP.INITIAL_DIAGNOSTICS;
            const Icon = isFlexiChip
              ? modeIconFor(DELIVERY_MODE.FLEXI)
              : isInCentreChip
                ? modeIconFor(DELIVERY_MODE.IN_CENTRE)
                : isDiagnosticsChip
                  ? statusIconFor(DISPLAY_STATUS.DIAGNOSTICS_COMPLETED)
                  : statusIconFor(status);
            const label = isFlexiChip
              ? FILTER_CHIP_META[FILTER_CHIP.FLEXI].label
              : isInCentreChip
                ? FILTER_CHIP_META[FILTER_CHIP.IN_CENTRE].label
                : isDiagnosticsChip
                  ? FILTER_CHIP_META[FILTER_CHIP.INITIAL_DIAGNOSTICS].label
                  : DISPLAY_STATUS_META[status].label;
            return (
              <button
                key={status}
                type="button"
                className={`cv2-chip${statusFilter.includes(status) ? ' is-active' : ''}`}
                onClick={() => onToggleStatus(status)}
              >
                <Icon className="cv2-icon" style={{ fontSize: 13 }} />
                {label}
              </button>
            );
          })}
        </div>

        <h3 className="cv2-panel__section-title">Notes</h3>
        {notes.map((note) => (
          <div className="cv2-note" key={note.pk_notes_key}>
            <p className="cv2-note__text">{note.nt_notes}</p>
            <div className="cv2-note__foot">
              <span>
                {note.created_on
                  ? Moment(note.created_on).format('DD-MM-YYYY HH:mm')
                  : ''}
              </span>
              <button
                type="button"
                className="cv2-note__delete"
                onClick={() => onDeleteNote(note.pk_notes_key)}
                aria-label="Delete note"
              >
                <DeleteOutlineIcon className="cv2-icon" />
              </button>
            </div>
          </div>
        ))}

        {notes.length === 0 ? <span className="cv2-legend__hint">No notes yet.</span> : null}

        <form className="cv2-note-form" onSubmit={handleNoteSubmit}>
          <input
            type="text"
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            placeholder="Add a note"
            aria-label="Add a note"
          />
          <button
            type="submit"
            className="cv2-btn cv2-btn--icon"
            disabled={isNoteSubmitting || noteDraft.trim() === ''}
            aria-label="Save note"
          >
            <AddIcon className="cv2-icon" />
          </button>
        </form>
      </div>
    </aside>
  );
}

FilterPanel.propTypes = {
  lanes: PropTypes.arrayOf(
    PropTypes.shape({
      tutorKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tutorName: PropTypes.string.isRequired,
      colorIndex: PropTypes.number.isRequired,
      lessonCount: PropTypes.number.isRequired,
    })
  ).isRequired,
  selectedTutorKeys: PropTypes.array.isRequired,
  onToggleTutor: PropTypes.func.isRequired,
  onSelectAllTutors: PropTypes.func.isRequired,
  onTutorClick: PropTypes.func.isRequired,
  statusFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  notes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAddNote: PropTypes.func.isRequired,
  onDeleteNote: PropTypes.func.isRequired,
  isNoteSubmitting: PropTypes.bool,
  requestCounts: PropTypes.shape({
    deferRequests: PropTypes.number,
    deferBin: PropTypes.number,
    oneOffRequests: PropTypes.number,
    disapproved: PropTypes.number,
  }),
  onOpenRequest: PropTypes.func.isRequired,
};

FilterPanel.defaultProps = {
  isNoteSubmitting: false,
  requestCounts: {
    deferRequests: 0,
    deferBin: 0,
    oneOffRequests: 0,
    disapproved: 0,
  },
};
